import { Component, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, OnInit, inject } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ElimReclassLocalService } from './elim-reclass-local.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

export interface ElimAccountModel {
  LOCAL_ACCT_KEY: string;
  SYSTEM_ACCT_KEY: string;
  LOCAL_ACCT: string;
  LOCAL_ACCT_DESC: string;
  ACCT_DD_DISPLAY: string;
  PRE_ELIM_GAAP_AMT_HO_CURR: number;
  CUR_ELIM_AMT: number;
  OTHER_ELIMS_AMT: number;
  POST_ELIM_GAAP_AMT_HO_CURR: number;
  EP_AMT: number;
  POST_ELIM_EP_AMT_HO_CURR: number;
  TYPE: string;
  TP_KEY: number;
  TP_ENTITY: string;
  TP_REP_PD: number;
  LE_COMBINATION_KEY: string;
  FEED_DISPLAY: string;
  ENABLEFEED: boolean;
  PREELIMGAAPLOADING: boolean;
  OTHERELIMSLOADING: boolean;
  POSTGAAPLOADING: boolean;
  POSTEPLOADING: boolean;
  TRANS_DETAILS_KEY: number;
  TRANS_HEADER_KEY: number;
  INVALID_OBJECT: boolean;
  ACCT_LE_TP: string;
  FEED_CHART_KEY: string;
  feedDtls: any;
  tpObject: any;
  open?: boolean;
  [key: string]: any;
}

@Component({
    selector: 'gtw-elim-reclass-local',
    templateUrl: './elim-reclass-local.component.html',
    styleUrls: ['./elim-reclass-local.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ElimReclassLocalComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly gtwSnackbarService = inject(GtwSnackbarService);
  private unsubscribe$ = new Subject<void>();
  private readonly contextSubs = new Subscription();
  private isInitialized = false;
  /** Bumped on each `init()` / destroy so slower HTTP callbacks cannot commit after a newer context. */
  private loadEpoch = 0;
  /** Latest-wins per row index for overlapping `getAmounts` calls. */
  private amountSeqByIndex: Record<number, number> = {};
  private acctSearchSeq = 0;
  private tpSearchSeq = 0;

  // Data
  rowData: any = {};
  accounts: ElimAccountModel[] = [];
  feedDtls: any[] = [];
  reclassArray: any[] = [];
  transKeysToDelete: any[] = [];

  // UI state
  title = 'Elimination Reclass Summary View';
  showElimForm = false;
  showFormLoader = true;
  minimise = false;
  existingLoading = false;
  noRecords = false;
  crudLoading = false;
  isMaxLimitReached = false;
  formSubmitted = false;
  codeCombination: any = null;

  // Autocomplete state. Account and trading partner keep independent active-row
  // indices so a stale result list from one cannot open on top of the other.
  acctSearchResults: any[] = [];
  tpSearchResults: any[] = [];
  activeAcctSearchIndex = -1;
  activeTpSearchIndex = -1;
  loadingAccounts = false;
  loadingTPs = false;

  // Form data
  ELIM_DESCRIPTION = '';
  ELIM_TYPE = 'B';
  isElimTypeDisabled = false;
  homeOffice = '';
  elimReclassTotal = 0;
  transStatus = 'new';
  TRANS_HEADER_KEY = 0;
  GROUP_TRANS_KEY: any = 0;

  // Config
  mainScreenKey = 0;
  sourceAcctType: number | null = null;
  hoCombinationKey = 0;

  // Dropdown data for selects
  accountDropdownData: any[] = [];
  tpDropdownData: any[] = [];

  private globalService: any;
  private GENERAL_CONFIG: any;
  private filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };

  constructor(
    private cdr: ChangeDetectorRef,
    private elimService: ElimReclassLocalService,
    private _snackBar: MatSnackBar,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.loadEpoch++;
        this.isInitialized = false;
        this.tryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.loadEpoch++;
        this.isInitialized = false;
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.loadEpoch++;
    this.contextSubs.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    // Pencil/CE payloads may be a plain row, `{ data: row }`, or a selection array.
    let row = firstGridRowObjectFromPayload(rawRow);
    if (
      row?.data &&
      typeof row.data === 'object' &&
      !Array.isArray(row.data) &&
      row.COMBINATION_KEY == null &&
      row.data.COMBINATION_KEY != null
    ) {
      row = row.data;
    }
    this.rowData = extractObject(row);
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) return;
    if (!this.filterParams || Object.keys(this.filterParams).length === 0) return;

    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    const epoch = ++this.loadEpoch;

    this.accounts = [];
    this.crudLoading = false;
    this.ELIM_DESCRIPTION = '';
    this.reclassArray = [];
    this.GROUP_TRANS_KEY = 0;
    this.existingLoading = false;
    this.noRecords = false;
    this.isElimTypeDisabled = false;
    this.transStatus = 'new';
    this.isMaxLimitReached = false;
    this.formSubmitted = false;
    this.transKeysToDelete = [];
    this.ELIM_TYPE = 'B';
    this.elimReclassTotal = 0;
    this.TRANS_HEADER_KEY = 0;
    this.acctSearchResults = [];
    this.tpSearchResults = [];
    this.activeAcctSearchIndex = -1;
    this.activeTpSearchIndex = -1;
    this.codeCombination = null;

    const screenKeyRaw = this.globalService?.globalParams?.screen_key
      || this.filterParams?.screen_key;
    this.mainScreenKey = screenKeyRaw ? parseInt(String(screenKeyRaw), 10) : 0;

    this.sourceAcctType = this.rowData.SYSTEM_ACCT_TYPE;
    this.hoCombinationKey = this.rowData.HO_COMBINATION_KEY;
    this.homeOffice = this.formatHomeOffice(this.rowData);
    this.loadHomeOfficeDetails(epoch);

    if (this.rowData.COMBINATION_KEY) {
      this.showElimForm = true;
      this.showFormLoader = false;
      this.loadFeeds(epoch);
      this.setupSourceAccount();
      this.loadExistingElims(epoch);

      this.elimService.getCodeCombination(this.GENERAL_CONFIG.base_url, this.rowData.COMBINATION_KEY)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (data: any) => {
            if (epoch !== this.loadEpoch) return;
            this.codeCombination = data;
            this.cdr.detectChanges();
          },
          () => {
            if (epoch !== this.loadEpoch) return;
            this.codeCombination = null;
            this.cdr.detectChanges();
          }
        );
    }

    this.cdr.detectChanges();
  }

  private formatHomeOffice(row: Record<string, unknown>, homeOffice: Record<string, unknown> = {}): string {
    const values = [
      row['HO_LEID'] === '' ? homeOffice['LEID'] : row['HO_LEID'] ?? homeOffice['LEID'],
      row['HO_CDR_NO'] === '' ? homeOffice['CDR_NO'] : row['HO_CDR_NO'] ?? homeOffice['CDR_NO'],
      row['HO_RPT_PD'] === '' ? homeOffice['REPORTING_PERIOD'] : row['HO_RPT_PD'] ?? homeOffice['REPORTING_PERIOD'],
      row['HO_CURR'] === '' ? homeOffice['CURR'] : row['HO_CURR'] ?? homeOffice['CURR']
    ];
    return values.map(value => value == null || value === '' ? '-' : String(value)).join('/');
  }

  private loadHomeOfficeDetails(epoch: number): void {
    const row = this.rowData;
    const missingDetails = ['HO_LEID', 'HO_CDR_NO', 'HO_RPT_PD', 'HO_CURR']
      .some(key => row[key] == null || row[key] === '');
    if (!missingDetails || !this.hoCombinationKey) return;

    // Resolve the HO combination, not the source account's combination/currency.
    this.elimService.getCodeCombination(this.GENERAL_CONFIG.base_url, this.hoCombinationKey)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (homeOffice: Record<string, unknown> | null) => {
          if (epoch !== this.loadEpoch) return;
          this.homeOffice = this.formatHomeOffice(row, homeOffice ?? {});
          this.cdr.markForCheck();
        },
        error: () => {
          // Retain the known row values and placeholders when lookup fails.
          if (epoch !== this.loadEpoch) return;
          this.cdr.markForCheck();
        }
      });
  }

  private loadFeeds(epoch: number): void {
    const taxYear = this.filterParams.tax_year || this.globalService?.globalParams?.tax_year;
    const scenario = this.filterParams.scenario || this.globalService?.globalParams?.scenario;

    this.elimService.getFeeds(
      this.GENERAL_CONFIG.base_url, taxYear, scenario, this.hoCombinationKey, this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        if (epoch !== this.loadEpoch) return;
        this.feedDtls = data;
        this.cdr.detectChanges();
      },
      () => {
        if (epoch !== this.loadEpoch) return;
        this.cdr.detectChanges();
      }
    );
  }

  private setupSourceAccount(): void {
    const rd = this.rowData;
    const feedDisplay = rd.LEID + ' - ' + (rd.MARS || rd.CDR_NO) + ' / ' + (rd.ME_CODE || rd.ME) + ' / ' + (rd.FC || rd.CURR_CODE);
    const acctLeTP = '' + rd.LOCAL_ACCT_KEY + rd.COMBINATION_KEY + rd.TP_KEY;

    const tpEntity = rd.TP_KEY === 0 ? '' : `${rd.TP_LEID}-${rd.TP_CDR_NO}(Pd:${rd.TP_REP_PD})`;

    const sourceRecord: ElimAccountModel = {
      LOCAL_ACCT_KEY: rd.LOCAL_ACCT_KEY,
      SYSTEM_ACCT_KEY: rd.SYSTEM_ACCT_KEY,
      LOCAL_ACCT: rd.LOCAL_ACCT,
      LOCAL_ACCT_DESC: rd.LOCAL_ACCT_DESC,
      ACCT_DD_DISPLAY: rd.LOCAL_ACCT + '-' + rd.LOCAL_ACCT_DESC,
      TP_KEY: rd.TP_KEY,
      TP_ENTITY: tpEntity,
      TP_REP_PD: rd.TP_KEY === 0 ? 0 : rd.TP_REP_PD,
      TYPE: 'source',
      LE_COMBINATION_KEY: rd.COMBINATION_KEY,
      FEED_DISPLAY: feedDisplay,
      ENABLEFEED: true,
      PREELIMGAAPLOADING: false,
      OTHERELIMSLOADING: false,
      POSTGAAPLOADING: false,
      POSTEPLOADING: false,
      TRANS_HEADER_KEY: 0,
      TRANS_DETAILS_KEY: 0,
      INVALID_OBJECT: false,
      ACCT_LE_TP: acctLeTP,
      FEED_CHART_KEY: rd.LOCAL_CHART_KEY,
      PRE_ELIM_GAAP_AMT_HO_CURR: 0,
      CUR_ELIM_AMT: 0,
      OTHER_ELIMS_AMT: 0,
      POST_ELIM_GAAP_AMT_HO_CURR: 0,
      EP_AMT: 0,
      POST_ELIM_EP_AMT_HO_CURR: 0,
      feedDtls: null,
      tpObject: null
    };

    this.accounts = [sourceRecord];
    this.addTargetAccount();
    this.getAmounts(0);
  }

  addTargetAccount(): void {
    const targetAcct: ElimAccountModel = {
      LOCAL_ACCT_KEY: '',
      SYSTEM_ACCT_KEY: '',
      LOCAL_ACCT: '',
      LOCAL_ACCT_DESC: '',
      ACCT_DD_DISPLAY: '',
      PRE_ELIM_GAAP_AMT_HO_CURR: 0,
      CUR_ELIM_AMT: 0,
      OTHER_ELIMS_AMT: 0,
      POST_ELIM_GAAP_AMT_HO_CURR: 0,
      EP_AMT: 0,
      POST_ELIM_EP_AMT_HO_CURR: 0,
      TYPE: 'blank',
      TP_KEY: 0,
      TP_ENTITY: '',
      TP_REP_PD: 0,
      LE_COMBINATION_KEY: '',
      FEED_DISPLAY: '',
      ENABLEFEED: false,
      PREELIMGAAPLOADING: false,
      OTHERELIMSLOADING: false,
      POSTGAAPLOADING: false,
      POSTEPLOADING: false,
      TRANS_DETAILS_KEY: 0,
      TRANS_HEADER_KEY: 0,
      INVALID_OBJECT: false,
      ACCT_LE_TP: '0',
      FEED_CHART_KEY: '',
      feedDtls: null,
      tpObject: null
    };

    this.accounts.push(targetAcct);
    this.cdr.detectChanges();
  }

  removeAccount(index: number): void {
    if (this.transStatus === 'edit') {
      this.transKeysToDelete.push({
        TRANS_DETAILS_KEY: this.accounts[index].TRANS_DETAILS_KEY,
        TRANS_HEADER_KEY: this.accounts[index].TRANS_HEADER_KEY
      });
    }
    this.accounts.splice(index, 1);
    this.recalcElimTotal();
    this.cdr.detectChanges();
  }

  feedSelected(feed: any, index: number): void {
    const curr = this.accounts[index];
    curr.LE_COMBINATION_KEY = feed.COMBINATION_KEY;
    curr.FEED_DISPLAY = `${feed.LEID} - ${feed.CDR_NO} / ${feed.ME_CODE} / ${feed.CURR}`;
    curr.ACCT_LE_TP = '' + curr.LOCAL_ACCT_KEY + feed.COMBINATION_KEY + curr.TP_KEY;
    curr.FEED_CHART_KEY = feed.LOCAL_CHART_KEY;
    curr.INVALID_OBJECT = false;
    this.cdr.detectChanges();
  }

  searchAccounts(event: any, index: number): void {
    const val = (event.target as HTMLInputElement).value;
    this.accounts[index].acctSearchText = val;
    if (!val || val.length < 3) {
      this.acctSearchResults = [];
      this.activeAcctSearchIndex = -1;
      this.cdr.detectChanges();
      return;
    }

    const curr = this.accounts[index];
    // Before a Feed is picked the row carries no combination/chart key; search the
    // source row's own entity and local chart rather than sending an empty key.
    const combinationKey = curr.LE_COMBINATION_KEY || this.rowData.COMBINATION_KEY;
    const rawChartKey = curr.FEED_CHART_KEY || this.rowData.LOCAL_CHART_KEY;
    const chartKey = rawChartKey ? +rawChartKey : null;

    this.activeAcctSearchIndex = index;
    this.loadingAccounts = true;

    const acctType = Number(this.sourceAcctType);
    const acctTypeStr = [1, 2, 3].includes(acctType) ? '1,2,3' : '5,6,7';
    const sourceType = this.rowData.SOURCE_SYSTEM === 'DCS' ? 'D' : 'F';
    const searchSeq = ++this.acctSearchSeq;

    this.elimService.getAccounts(
      this.GENERAL_CONFIG.base_url,
      this.mainScreenKey,
      combinationKey,
      chartKey,
      val,
      acctTypeStr,
      sourceType,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        if (searchSeq !== this.acctSearchSeq) return;
        this.acctSearchResults = data || [];
        this.loadingAccounts = false;
        this.cdr.detectChanges();
      },
      () => {
        if (searchSeq !== this.acctSearchSeq) return;
        this.acctSearchResults = [];
        this.loadingAccounts = false;
        this.notifySearchFailure('account');
        this.cdr.detectChanges();
      }
    );
  }

  closeAcctOverlay(): void {
    if (this.acctSearchResults.length || this.loadingAccounts) {
      this.acctSearchResults = [];
      this.activeAcctSearchIndex = -1;
      this.cdr.detectChanges();
    }
  }

  closeTpOverlay(): void {
    if (this.tpSearchResults.length || this.loadingTPs) {
      this.tpSearchResults = [];
      this.activeTpSearchIndex = -1;
      this.cdr.detectChanges();
    }
  }

  selectAccount(account: any, index: number): void {
    const curr = this.accounts[index];
    curr.LOCAL_ACCT_KEY = account.LOCAL_ACCT_KEY;
    curr.SYSTEM_ACCT_KEY = account.SYSTEM_ACCT_KEY;
    curr.LOCAL_ACCT = account.LOCAL_ACCT;
    curr.LOCAL_ACCT_DESC = account.LOCAL_ACCT_DESC;
    curr.ACCT_DD_DISPLAY = account.ACCT_DD_DISPLAY || `${account.LOCAL_ACCT} - ${account.LOCAL_ACCT_DESC}`;
    curr.TP_KEY = account.TP_KEY;
    curr.TYPE = 'target';
    curr.ENABLEFEED = true;
    curr.ACCT_LE_TP = '' + account.LOCAL_ACCT_KEY + curr.LE_COMBINATION_KEY + account.TP_KEY;
    curr.INVALID_OBJECT = false;
    curr.acctSearchText = curr.ACCT_DD_DISPLAY;

    if (account.TP_KEY !== 0) {
      curr.tpObject = account;
      curr.TP_ENTITY = `${account.TP_LEID}-${account.TP_CDR_NO}(Pd:${account.TP_REP_PD})`;
      curr.tpSearchText = curr.TP_ENTITY;
    }

    this.acctSearchResults = [];
    this.activeAcctSearchIndex = -1;
    this.getAmounts(index);
    this.cdr.detectChanges();
  }

  searchTradingPartners(event: any, index: number): void {
    const val = (event.target as HTMLInputElement).value;
    this.accounts[index].tpSearchText = val;
    if (!val || val.length < 3) {
      this.tpSearchResults = [];
      this.activeTpSearchIndex = -1;
      this.cdr.detectChanges();
      return;
    }

    this.activeTpSearchIndex = index;
    this.loadingTPs = true;
    const tpSeq = ++this.tpSearchSeq;
    const taxYear = this.filterParams.tax_year
      || this.globalService?.globalParams?.tax_year
      || this.rowData.TAX_YEAR;
    const scenario = this.filterParams.scenario
      || this.globalService?.globalParams?.scenario
      || this.rowData.SCENARIO;

    this.elimService.getTradingPartners(
      this.GENERAL_CONFIG.base_url, taxYear, scenario, val, this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        if (tpSeq !== this.tpSearchSeq) return;
        this.tpSearchResults = data || [];
        this.loadingTPs = false;
        this.cdr.detectChanges();
      },
      () => {
        if (tpSeq !== this.tpSearchSeq) return;
        this.tpSearchResults = [];
        this.loadingTPs = false;
        this.notifySearchFailure('trading partner');
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Both typeaheads previously discarded backend failures, so a rejected query was
   * indistinguishable from a genuinely empty result set.
   */
  private notifySearchFailure(label: string): void {
    this.gtwSnackbarService.openSnackBar(
      `Unable to load ${label} results.`,
      'gtw-snackbar--red'
    );
  }

  selectTradingPartner(tp: any, index: number): void {
    if (!tp || tp.TP_KEY === 0) return;
    const curr = this.accounts[index];
    curr.TP_ENTITY = `${tp.TP_LEID}-${tp.TP_CDR_NO}(Pd:${tp.TP_REP_PD})`;
    curr.TP_KEY = tp.TP_KEY;
    curr.TP_REP_PD = tp.TP_REP_PD;
    curr.ACCT_LE_TP = '' + curr.LOCAL_ACCT_KEY + curr.LE_COMBINATION_KEY + tp.TP_KEY;
    curr.INVALID_OBJECT = false;
    curr.tpSearchText = curr.TP_ENTITY;

    this.tpSearchResults = [];
    this.activeTpSearchIndex = -1;
    this.getAmounts(index);
    this.cdr.detectChanges();
  }

  private getAmounts(index: number): void {
    const curr = this.accounts[index];
    if (!curr.LE_COMBINATION_KEY || !curr.LOCAL_ACCT_KEY) return;

    const amountSeq = (this.amountSeqByIndex[index] = (this.amountSeqByIndex[index] || 0) + 1);

    curr.PREELIMGAAPLOADING = true;
    curr.OTHERELIMSLOADING = true;
    curr.POSTGAAPLOADING = true;
    curr.POSTEPLOADING = true;

    this.elimService.getAmounts(
      this.GENERAL_CONFIG.base_url,
      +curr.LE_COMBINATION_KEY,
      this.hoCombinationKey,
      +curr.LOCAL_ACCT_KEY,
      curr.TP_KEY,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        if (amountSeq !== this.amountSeqByIndex[index]) return;
        curr.PREELIMGAAPLOADING = false;
        curr.OTHERELIMSLOADING = false;
        curr.POSTGAAPLOADING = false;
        curr.POSTEPLOADING = false;
        if (data.length) {
          curr.PRE_ELIM_GAAP_AMT_HO_CURR = data[0].PRE_ELIM_GAAP_AMT_HO_CURR;
          curr.CUR_ELIM_AMT = data[0].CUR_ELIM_ADJ;
          curr.OTHER_ELIMS_AMT = data[0].OTHER_ELIM_ADJS;
          curr.POST_ELIM_GAAP_AMT_HO_CURR = data[0].POST_ELIM_GAAP_AMT_HO_CURR;
          curr.EP_AMT = data[0].PRE_ELIM_TAX_AMT_HO_CURR;
          curr.POST_ELIM_EP_AMT_HO_CURR = data[0].POST_ELIM_EP_AMT_HO_CURR;
        }
        this.recalcElimTotal();
        this.cdr.detectChanges();
      },
      () => {
        if (amountSeq !== this.amountSeqByIndex[index]) return;
        curr.PREELIMGAAPLOADING = false;
        curr.OTHERELIMSLOADING = false;
        curr.POSTGAAPLOADING = false;
        curr.POSTEPLOADING = false;
        this.cdr.detectChanges();
      }
    );
  }

  setPostElimTotal(index: number): void {
    const acct = this.accounts[index];
    acct.POST_ELIM_GAAP_AMT_HO_CURR = parseFloat(acct.PRE_ELIM_GAAP_AMT_HO_CURR as any) + parseFloat(acct.OTHER_ELIMS_AMT as any);
    acct.POST_ELIM_EP_AMT_HO_CURR = parseFloat(acct.PRE_ELIM_GAAP_AMT_HO_CURR as any) + parseFloat(acct.OTHER_ELIMS_AMT as any) + parseFloat(acct.EP_AMT as any);
    if (acct.CUR_ELIM_AMT !== undefined) {
      acct.POST_ELIM_GAAP_AMT_HO_CURR += parseFloat(acct.CUR_ELIM_AMT as any);
      acct.POST_ELIM_EP_AMT_HO_CURR += parseFloat(acct.CUR_ELIM_AMT as any);
    }
    this.recalcElimTotal();
    this.cdr.detectChanges();
  }

  private recalcElimTotal(): void {
    let total = 0;
    this.accounts.forEach(acct => {
      if (acct.CUR_ELIM_AMT && !isNaN(+acct.CUR_ELIM_AMT)) {
        total += parseFloat(acct.CUR_ELIM_AMT as any);
      }
    });
    this.elimReclassTotal = total;
  }

  validateDesc(): void {
    this.isMaxLimitReached = this.ELIM_DESCRIPTION.length >= 250;
  }

  removeMessage(): void {
    this.isMaxLimitReached = false;
  }

  // Existing elims
  private loadExistingElims(epoch: number): void {
    this.existingLoading = true;
    this.elimService.loadExistingElims(
      this.GENERAL_CONFIG.base_url,
      this.mainScreenKey,
      this.rowData.LOCAL_CHART_KEY,
      this.hoCombinationKey,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        if (epoch !== this.loadEpoch) return;
        this.reclassArray = this.formatReclassData(data);
        this.existingLoading = false;
        this.noRecords = this.reclassArray.length === 0;
        this.cdr.detectChanges();
      },
      () => {
        if (epoch !== this.loadEpoch) return;
        this.existingLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  private formatReclassData(data: any[]): any[] {
    const reclasses: any[] = [];
    const accounts: any[] = [];

    data.forEach(val => {
      if (+val.ROW_INDICATOR === 1) {
        const obj = { ...val, ACCOUNTS: [], open: false };
        reclasses.push(obj);
      } else {
        accounts.push({ ...val, TYPE: 'source' });
      }
    });

    reclasses.forEach(reclass => {
      reclass.ACCOUNTS = accounts.filter(
        acc => +acc.ROW_INDICATOR !== 1 && acc.GROUP_TRANS_KEY === reclass.GROUP_TRANS_KEY
      );
    });

    return reclasses;
  }

  showDetails(index: number, show: boolean): void {
    this.reclassArray[index].open = show;
    this.cdr.detectChanges();
  }

  loadReclass(index: number): void {
    const data = this.reclassArray[index];
    this.ELIM_DESCRIPTION = data.ADJ_DESC;
    this.GROUP_TRANS_KEY = data.GROUP_TRANS_KEY;
    this.TRANS_HEADER_KEY = data.TRANS_HEADER_KEY;
    this.ELIM_TYPE = data.ADJ_TYPE;
    this.accounts = data.ACCOUNTS.map((acc: any) => ({
      ...acc,
      acctSearchText: acc.LOCAL_ACCT ? `${acc.LOCAL_ACCT} - ${acc.LOCAL_ACCT_DESC}` : '',
      tpSearchText: acc.TP_ENTITY || ''
    }));
    this.isElimTypeDisabled = true;
    this.transStatus = 'edit';

    this.accounts.forEach((_: any, key: number) => {
      this.setPostElimTotal(key);
    });

    this.recalcElimTotal();
    this.cdr.detectChanges();
  }

  deleteReclass(reclass: any): void {
    const epoch = this.loadEpoch;
    this.existingLoading = true;
    const deleteData = {
      group_trans_key: reclass.GROUP_TRANS_KEY,
      issue_key: reclass.ISSUE_ID,
      type: 'D',
      combination_key: this.rowData.COMBINATION_KEY,
      chart_key: this.rowData.CHART_KEY
    };

    this.elimService.deleteElims(
      this.GENERAL_CONFIG.base_url, deleteData, this.globalService, this.filterParams
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (response: any) => {
        if (epoch !== this.loadEpoch) {
          this.existingLoading = false;
          this.cdr.detectChanges();
          return;
        }
        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
          this.existingLoading = false;
        } else {
          this._snackBar.open('Reclass has been successfully deleted', 'Close', { duration: 4000, panelClass: ['success-snackbar'] });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      () => {
        if (epoch !== this.loadEpoch) {
          this.existingLoading = false;
          this.cdr.detectChanges();
          return;
        }
        this._snackBar.open('Error deleting reclass', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
        this.existingLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  save(): void {
    this.formSubmitted = true;
    if (!this.ELIM_DESCRIPTION || this.ELIM_DESCRIPTION.trim() === '') {
      this._snackBar.open('Elimination Description is required', 'Close', { duration: 4000, panelClass: ['warning-snackbar'] });
      this.cdr.detectChanges();
      return;
    }

    if (this.accounts.length <= 1) {
      this._snackBar.open('There should be more than one account to add Eliminations', 'Close', { duration: 4000, panelClass: ['warning-snackbar'] });
      return;
    }

    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', { duration: 4000, panelClass: ['warning-snackbar'] });
      return;
    }

    this.crudLoading = true;

    const epoch = this.loadEpoch;

    const txIssueId = this.globalService?.globalParams?.issue_id_for_tx_save;
    const issueKey = txIssueId === undefined || txIssueId === ''
      ? (this.globalService?.globalParams?.issue_id === undefined ? null : parseInt(this.globalService.globalParams.issue_id, 10))
      : txIssueId;

    const returnObj: any = {
      ELIM_DESCRIPTION: this.ELIM_DESCRIPTION,
      tax_year: this.filterParams.tax_year,
      ISSUE_KEY: issueKey,
      ADJ_TYPE: this.ELIM_TYPE,
      TYPE: this.GROUP_TRANS_KEY === 0 ? 'S' : 'U',
      GROUP_TRANS_KEY: this.GROUP_TRANS_KEY === 0 ? 0 : this.GROUP_TRANS_KEY,
      ADJ_ORIGIN_SOURCE: this.rowData.SOURCE_SYSTEM,
      SOURCE_SYSTEM_FLAG: this.rowData.SOURCE_SYSTEM === 'DCS' ? 'D' : 'F',
      TRANS_KEYS_TO_DEL: this.transKeysToDelete
    };

    const accountArray = this.accounts.map(acct => ({
      TRANS_HEADER_KEY: acct.TRANS_HEADER_KEY,
      ACCOUNT_CODE: acct.LOCAL_ACCT,
      COMBINATION_KEY: acct.LE_COMBINATION_KEY,
      ACCT_KEY: acct.LOCAL_ACCT_KEY,
      TP_KEY: acct.TP_KEY,
      AMOUNT: acct.CUR_ELIM_AMT,
      TRANS_DETAILS_KEY: acct.TRANS_DETAILS_KEY,
      FEED_CHART_KEY: acct.FEED_CHART_KEY
    }));

    this.elimService.saveUpdateEliminations(
      this.GENERAL_CONFIG.base_url, [returnObj], accountArray, this.globalService, this.filterParams
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (response: any) => {
        if (epoch !== this.loadEpoch) {
          this.crudLoading = false;
          this.cdr.detectChanges();
          return;
        }
        if (this.globalService?.globalParams) {
          this.globalService.globalParams.issue_id_for_tx_save = '';
        }
        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
          this.crudLoading = false;
        } else {
          this._snackBar.open('Eliminations have been successfully saved', 'Close', { duration: 4000, panelClass: ['success-snackbar'] });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      () => {
        if (epoch !== this.loadEpoch) {
          this.crudLoading = false;
          this.cdr.detectChanges();
          return;
        }
        this._snackBar.open('Error saving eliminations', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
        this.crudLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  reset(): void {
    this.init();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  toggleMinimise(): void {
    this.minimise = !this.minimise;
    this.cdr.detectChanges();
  }

  formatNumber(val: any): string {
    if (val === null || val === undefined) return '';
    const num = parseFloat(val);
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
}
